import React, { useCallback } from 'react';
import { ChatAction, ChatMessage as ChatMessageType } from '../types/chat';
import { validateActionContext, validateMonitorActionParams } from './chatActions/actionValidation';
import {
  executePreviewDataset,
  executeGenerateDataset,
  executeFindMoreResults,
  executeEnrichColumn,
  executeSendEmails,
  ActionExecutorContext
} from './chatActions/actionExecutors';
import {
  executeCreateMonitor,
  executeCreateMonitorWithDataset,
  executeListMonitors,
  executeUpdateMonitor,
  executeDeleteMonitor,
  executeListMonitorRuns
} from './chatActions/monitorActionExecutors';

interface UseChatActionsProps {
  onActionTriggered?: (action: { type: string; payload: any }) => void;
  tableId?: string;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>;
  getSessionId: () => string | null;
}

export const useChatActions = ({
  onActionTriggered,
  tableId,
  setMessages,
  getSessionId
}: UseChatActionsProps) => {
  const executeAction = useCallback(async (action: ChatAction) => {
    try {
      // Validate action context
      validateActionContext(action.type, tableId);

      // Add a user message showing what action they chose
      const userChoiceMessage: ChatMessageType = {
        id: Date.now().toString(),
        role: 'user',
        content: action.description || action.type.replace(/_/g, ' '),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userChoiceMessage]);

      // Create context for action executors
      const context: ActionExecutorContext = {
        tableId,
        setMessages,
        onActionTriggered,
        getSessionId
      };
      
      // Execute the appropriate action
      const actionType = action.type.toLowerCase();
      
      switch (actionType) {
        case 'preview_dataset':
          await executePreviewDataset(action, context);
          break;
        case 'generate_dataset':
          executeGenerateDataset(action, context);
          break;
        case 'find_more_results':
          executeFindMoreResults(action, context);
          break;
        case 'enrich_column':
          executeEnrichColumn(action, context);
          break;
        case 'send_emails':
          executeSendEmails(action, context);
          break;
        // Monitor actions
        case 'create_monitor':
          await executeCreateMonitor(action, context);
          break;
        case 'create_monitor_with_dataset':
          validateMonitorActionParams(actionType, action.parameters);
          await executeCreateMonitorWithDataset(action, context);
          break;
        case 'list_monitors':
          await executeListMonitors(action, context);
          break;
        case 'update_monitor':
          validateMonitorActionParams(actionType, action.parameters);
          await executeUpdateMonitor(action, context);
          break;
        case 'delete_monitor':
          validateMonitorActionParams(actionType, action.parameters);
          await executeDeleteMonitor(action, context);
          break;
        case 'list_monitor_runs':
          validateMonitorActionParams(actionType, action.parameters);
          await executeListMonitorRuns(action, context);
          break;
        default:
          throw new Error(`Unknown action type: ${action.type}`);
      }
    } catch (error) {
      console.error('Error executing action:', error);
      
      // Add error message
      const errorMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `❌ Error executing action: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  }, [onActionTriggered, tableId, setMessages, getSessionId]);

  return { executeAction };
};